/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useNextRouter } from '@/data/Content/_NextRouter';
import { useNotifications } from '@/data/Content/Notifications';
import { useCurrentRoute } from 'integration/hooks/useCurrentRoute';
import { useEffect, useRef } from 'react';

const isRefreshedRoute = (route: string) =>
	route === '/' || route === '/home-es' || route === '/home';

/**
 * Hook que muestra una notificación de sesión expirada cuando hasSessionError es true.
 * La notificación solo se muestra una vez por carga de página.
 *
 * @param hasSessionError - Indica si hay un error de sesión
 */
export const useSessionErrorNotification = (hasSessionError?: boolean) => {
	const { showMessage } = useNotifications();
	const currentRouter = useCurrentRoute();
	const router = useNextRouter();
	const hasShownSessionError = useRef(false);
	const hasRefreshed = useRef(false);

	useEffect(() => {
		if (hasSessionError && !hasRefreshed.current && isRefreshedRoute(currentRouter)) {
			router.reload();
			hasRefreshed.current = true;
		}
	}, [hasSessionError, currentRouter, router]);

	useEffect(() => {
		if (hasSessionError && !hasShownSessionError.current) {
			showMessage('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
				severity: 'snackbar-error',
				anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
			});
			hasShownSessionError.current = true;
		}
	}, [hasSessionError, showMessage]);
};
